import type {
  PersonalManagerDayRow,
  PersonalManagerTaskCode,
  PersonalManagerTaskDisplayStatus,
  PersonalManagerTaskStatus,
} from '@/types'

export type PersonalManagerTaskField =
  | 'app_status'
  | 'partner_report_status'
  | 'standup_status'
  | 'admins_status'
  | 'managers_status'
  | 'teachers_status'
  | 'revenue_status'
  | 'partner_notified_status'
  | 'burpee_status'
  | 'content_status'
  | 'reconciliation_status'

export interface PersonalManagerTaskDefinition {
  key: 'app_filled' | PersonalManagerTaskCode
  label: string
  field: PersonalManagerTaskField
  deadlineTimeMsk: string
  editable: boolean
  auto: boolean
  skipOnSunday?: boolean
}

export interface PersonalManagerTaskBlockDefinition {
  key: 'reports' | 'revenue' | 'application' | 'social' | 'sales' | 'other'
  label: string
  taskKeys: Array<PersonalManagerTaskDefinition['key']>
}

export interface PersonalManagerTaskScopeRow {
  key: string
  representativeRow: PersonalManagerDayRow
  rows: PersonalManagerDayRow[]
  multiCityPartner: boolean
  splitByCity: boolean
  cityName: string | null
}

export const PERSONAL_MANAGER_TASKS: PersonalManagerTaskDefinition[] = [
  { key: 'app_filled', label: 'В приложении всё заполнено', field: 'app_status', deadlineTimeMsk: '08:30', editable: false, auto: true },
  { key: 'partner_report', label: 'Отчёт в чат', field: 'partner_report_status', deadlineTimeMsk: '08:30', editable: true, auto: false },
  { key: 'standup', label: 'Планёрка', field: 'standup_status', deadlineTimeMsk: '09:30', editable: true, auto: false, skipOnSunday: true },
  { key: 'admins_filled', label: 'Администраторы заполнены', field: 'admins_status', deadlineTimeMsk: '10:30', editable: true, auto: false },
  { key: 'managers_filled', label: 'Менеджеры заполнены', field: 'managers_status', deadlineTimeMsk: '10:30', editable: true, auto: false },
  { key: 'teachers_filled', label: 'Педагоги заполнены', field: 'teachers_status', deadlineTimeMsk: '10:30', editable: true, auto: false },
  { key: 'revenue_matches', label: 'Выручка сходится', field: 'revenue_status', deadlineTimeMsk: '13:00', editable: true, auto: false },
  { key: 'partner_notified', label: 'Франчайзи отписались по выручке', field: 'partner_notified_status', deadlineTimeMsk: '14:00', editable: true, auto: false },
  { key: 'burpee', label: 'Берпи', field: 'burpee_status', deadlineTimeMsk: '15:00', editable: true, auto: false },
  { key: 'content_posted', label: 'Контент выложен', field: 'content_status', deadlineTimeMsk: '14:30', editable: true, auto: false },
  { key: 'reconciliation_table', label: 'Таблица сверки заполнена', field: 'reconciliation_status', deadlineTimeMsk: '17:00', editable: true, auto: false },
]

export function getPersonalManagerTaskDeadlineLabel(task: Pick<PersonalManagerTaskDefinition, 'key' | 'deadlineTimeMsk'>): string {
  if (task.key === 'app_filled') return '10:00 местного времени'
  return `${task.deadlineTimeMsk} МСК`
}

export const PERSONAL_MANAGER_TASK_BLOCKS: PersonalManagerTaskBlockDefinition[] = [
  { key: 'reports', label: 'Отчёты', taskKeys: ['partner_report'] },
  { key: 'revenue', label: 'Выручка', taskKeys: ['revenue_matches'] },
  { key: 'application', label: 'Приложение', taskKeys: ['app_filled', 'admins_filled', 'managers_filled', 'teachers_filled'] },
  { key: 'social', label: 'Соцсети', taskKeys: ['content_posted'] },
  { key: 'sales', label: 'Продажи', taskKeys: ['standup', 'partner_notified'] },
  { key: 'other', label: 'Прочее', taskKeys: ['burpee', 'reconciliation_table'] },
]

const PARTNER_SCOPED_TASK_KEYS = new Set<PersonalManagerTaskDefinition['key']>([
  'standup',
  'burpee',
  'content_posted',
])

const EMPTY_CITY_KEY = '__no_city__'

function comparePersonalManagerRows(a: PersonalManagerDayRow, b: PersonalManagerDayRow): number {
  const cityCompare = String(a.city_name ?? '').localeCompare(String(b.city_name ?? ''), 'ru', { sensitivity: 'base' })
  if (cityCompare !== 0) return cityCompare

  const studioCompare = String(a.studio_number ?? a.studio_internal_id).localeCompare(
    String(b.studio_number ?? b.studio_internal_id),
    'ru',
    { numeric: true, sensitivity: 'base' }
  )
  if (studioCompare !== 0) return studioCompare

  return a.partner_number - b.partner_number
}

function normalizeCityName(cityName: string | null | undefined): string {
  const normalized = cityName?.trim()
  return normalized ? normalized : EMPTY_CITY_KEY
}

function getPartnerCityCounts(rows: PersonalManagerDayRow[]): Map<number, number> {
  const partnerCities = new Map<number, Set<string>>()

  for (const row of rows) {
    const cities = partnerCities.get(row.partner_number) ?? new Set<string>()
    cities.add(normalizeCityName(row.city_name))
    partnerCities.set(row.partner_number, cities)
  }

  return new Map(
    [...partnerCities.entries()].map(([partnerNumber, cities]) => [partnerNumber, cities.size])
  )
}

export function todayMskISO(): string {
  return new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString().slice(0, 10)
}

export function isSunday(taskDate: string): boolean {
  return new Date(`${taskDate}T00:00:00Z`).getUTCDay() === 0
}

export function getVisiblePersonalManagerTasks(taskDate: string): PersonalManagerTaskDefinition[] {
  return PERSONAL_MANAGER_TASKS.filter(task => !(task.skipOnSunday && isSunday(taskDate)))
}

export function getVisiblePersonalManagerTaskBlocks(taskDate: string): Array<PersonalManagerTaskBlockDefinition & { tasks: PersonalManagerTaskDefinition[] }> {
  const tasks = getVisiblePersonalManagerTasks(taskDate)
  return PERSONAL_MANAGER_TASK_BLOCKS
    .map(block => ({
      ...block,
      tasks: tasks.filter(task => block.taskKeys.includes(task.key)),
    }))
    .filter(block => block.tasks.length > 0)
}

export function isPersonalManagerTaskDone(status: PersonalManagerTaskDisplayStatus): boolean {
  return status === 'done' || status === 'day_off'
}

export function isPersonalManagerTaskFilled(
  status: PersonalManagerTaskDisplayStatus,
  task?: Pick<PersonalManagerTaskDefinition, 'key'>
): boolean {
  if (task?.key === 'app_filled') {
    return isPersonalManagerTaskDone(status) || status === 'late_done'
  }

  return status !== 'pending'
}

export function getPersonalManagerTaskStatus(
  row: PersonalManagerDayRow,
  task: Pick<PersonalManagerTaskDefinition, 'field'>
): PersonalManagerTaskDisplayStatus {
  return row[task.field] as PersonalManagerTaskDisplayStatus
}

export function getPersonalManagerTaskMarkStatus(
  row: PersonalManagerDayRow,
  task: Pick<PersonalManagerTaskDefinition, 'field'>
): PersonalManagerTaskStatus | null {
  switch (task.field) {
    case 'partner_report_status':
      return row.partner_report_mark_status
    case 'standup_status':
      return row.standup_mark_status
    case 'admins_status':
      return row.admins_mark_status
    case 'managers_status':
      return row.managers_mark_status
    case 'teachers_status':
      return row.teachers_mark_status
    case 'revenue_status':
      return row.revenue_mark_status
    case 'partner_notified_status':
      return row.partner_notified_mark_status
    case 'burpee_status':
      return row.burpee_mark_status
    case 'content_status':
      return row.content_mark_status
    case 'reconciliation_status':
      return row.reconciliation_mark_status
    default:
      return null
  }
}

export function isPartnerScopedPersonalManagerTask(key: PersonalManagerTaskDefinition['key']): boolean {
  return PARTNER_SCOPED_TASK_KEYS.has(key)
}

export function isCityScopedPersonalManagerTask(key: PersonalManagerTaskDefinition['key']): boolean {
  return key === 'content_posted'
}

export function getPersonalManagerTaskScopeRows(
  rows: PersonalManagerDayRow[],
  task: Pick<PersonalManagerTaskDefinition, 'key'>
): PersonalManagerTaskScopeRow[] {
  const sortedRows = [...rows].sort(comparePersonalManagerRows)

  if (!isPartnerScopedPersonalManagerTask(task.key)) {
    return sortedRows.map(row => ({
      key: `studio:${row.studio_internal_id}`,
      representativeRow: row,
      rows: [row],
      multiCityPartner: false,
      splitByCity: false,
      cityName: row.city_name,
    }))
  }

  const partnerCityCounts = getPartnerCityCounts(sortedRows)
  const groups = new Map<string, PersonalManagerTaskScopeRow>()

  for (const row of sortedRows) {
    const multiCityPartner = (partnerCityCounts.get(row.partner_number) ?? 0) > 1
    const cityKey = normalizeCityName(row.city_name)
    const splitByCity = multiCityPartner && isCityScopedPersonalManagerTask(task.key)
    const key = splitByCity
      ? `partner-city:${row.partner_number}:${cityKey}`
      : `partner:${row.partner_number}`

    const existing = groups.get(key)
    if (existing) {
      existing.rows.push(row)
      continue
    }

    groups.set(key, {
      key,
      representativeRow: row,
      rows: [row],
      multiCityPartner,
      splitByCity,
      cityName: cityKey === EMPTY_CITY_KEY ? null : row.city_name,
    })
  }

  return [...groups.values()]
}

export function getPersonalManagerTaskStatusForRows(
  rows: PersonalManagerDayRow[],
  task: Pick<PersonalManagerTaskDefinition, 'field'>
): PersonalManagerTaskDisplayStatus {
  if (rows.length === 0) return 'pending'

  const statuses = rows.map(row => getPersonalManagerTaskStatus(row, task))

  if (statuses.every(status => status === 'day_off')) return 'day_off'
  if (statuses.every(status => status === 'done' || status === 'day_off')) return 'done'
  if (statuses.every(status => status === 'late_done' || status === 'done' || status === 'day_off') && statuses.some(status => status === 'late_done')) {
    return 'late_done'
  }
  if (statuses.every(status => status === 'pending')) return 'pending'

  return 'not_done'
}

export function getPersonalManagerTaskProgress(
  rows: PersonalManagerDayRow[],
  task: Pick<PersonalManagerTaskDefinition, 'key' | 'field'>
): { done: number; total: number } {
  const scopeRows = getPersonalManagerTaskScopeRows(rows, task)
  const done = scopeRows.reduce((sum, scopeRow) => {
    return sum + (isPersonalManagerTaskDone(getPersonalManagerTaskStatusForRows(scopeRow.rows, task)) ? 1 : 0)
  }, 0)

  return {
    done,
    total: scopeRows.length,
  }
}

export function getPersonalManagerTaskFillProgress(
  rows: PersonalManagerDayRow[],
  task: Pick<PersonalManagerTaskDefinition, 'key' | 'field'>
): { filled: number; total: number } {
  const scopeRows = getPersonalManagerTaskScopeRows(rows, task)
  const filled = scopeRows.reduce((sum, scopeRow) => {
    return sum + (isPersonalManagerTaskFilled(getPersonalManagerTaskStatusForRows(scopeRow.rows, task), task) ? 1 : 0)
  }, 0)

  return {
    filled,
    total: scopeRows.length,
  }
}

export function getPersonalManagerTaskByKey(taskDate: string, key: PersonalManagerTaskDefinition['key']): PersonalManagerTaskDefinition | null {
  return getVisiblePersonalManagerTasks(taskDate).find(task => task.key === key) ?? null
}

export function getPersonalManagerBlockProgress(
  row: PersonalManagerDayRow,
  block: { tasks: Array<Pick<PersonalManagerTaskDefinition, 'field'>> }
): { done: number; total: number } {
  const total = block.tasks.length
  const done = block.tasks.reduce((sum, task) => {
    return sum + (isPersonalManagerTaskDone(getPersonalManagerTaskStatus(row, task)) ? 1 : 0)
  }, 0)
  return { done, total }
}

export function getPersonalManagerRowTaskPlan(taskDate: string): number {
  return getVisiblePersonalManagerTasks(taskDate).length
}

export function getPersonalManagerRowTaskFact(row: PersonalManagerDayRow): number {
  return getVisiblePersonalManagerTasks(row.task_date).reduce((sum, task) => {
    return sum + (isPersonalManagerTaskDone(getPersonalManagerTaskStatus(row, task)) ? 1 : 0)
  }, 0)
}

export function getPersonalManagerRowTaskFilledCount(row: PersonalManagerDayRow): number {
  return getVisiblePersonalManagerTasks(row.task_date).reduce((sum, task) => {
    return sum + (isPersonalManagerTaskFilled(getPersonalManagerTaskStatus(row, task), task) ? 1 : 0)
  }, 0)
}

export function getPersonalManagerRowsSummary(
  rows: PersonalManagerDayRow[],
  taskDate: string
): { plan: number; fact: number; pct: number } {
  const visibleTasks = getVisiblePersonalManagerTasks(taskDate)

  const plan = visibleTasks.reduce((sum, task) => {
    return sum + getPersonalManagerTaskScopeRows(rows, task).length
  }, 0)

  const fact = visibleTasks.reduce((sum, task) => {
    return sum + getPersonalManagerTaskScopeRows(rows, task).reduce((taskSum, scopeRow) => {
      return taskSum + (isPersonalManagerTaskDone(getPersonalManagerTaskStatusForRows(scopeRow.rows, task)) ? 1 : 0)
    }, 0)
  }, 0)

  return {
    plan,
    fact,
    pct: plan > 0 ? (fact / plan) * 100 : 0,
  }
}

export function getPersonalManagerRowsTaskCounters(
  rows: PersonalManagerDayRow[],
  taskDate: string
): { done: number; pending: number; problem: number; total: number } {
  const visibleTasks = getVisiblePersonalManagerTasks(taskDate)

  return visibleTasks.reduce(
    (acc, task) => {
      for (const scopeRow of getPersonalManagerTaskScopeRows(rows, task)) {
        const status = getPersonalManagerTaskStatusForRows(scopeRow.rows, task)
        acc.total += 1

        if (isPersonalManagerTaskDone(status)) {
          acc.done += 1
        } else if (status === 'pending') {
          acc.pending += 1
        } else {
          acc.problem += 1
        }
      }

      return acc
    },
    { done: 0, pending: 0, problem: 0, total: 0 }
  )
}
